import React, { useEffect, useMemo, useState } from 'react';
import { AlertCircle, CircleDot, Gauge, Loader2, UserRound } from 'lucide-react';
import { useAuth } from '../../../firebase/AuthProvider';
import Ticket from '../../../interfaces/ticket/Ticket';
import { getTicketById } from '../../../services/ticketServices/TicketService';

interface TicketProfileTooltipProps {
    publicId: string;
    theme?: 'light' | 'dark';
}

const TicketProfileTooltip: React.FC<TicketProfileTooltipProps> = ({ publicId, theme = 'light' }) => {
    const { currentUser, impersonatedUserId, currentOrganization, currentPolicies, currentUserInfo } = useAuth();
    const authScope = useMemo(() => ({ currentUser, impersonatedUserId, currentOrganization, currentPolicies, currentUserInfo }),
        [currentUser, impersonatedUserId, currentOrganization, currentPolicies, currentUserInfo]);
    const [resultState, setResultState] = useState<{ value: Ticket; scope: typeof authScope; reference: string } | null>(null);
    const ticket = resultState?.scope === authScope && resultState?.reference === publicId ? resultState.value : null;
    const [isLoading, setIsLoading] = useState(!ticket);
    const [hasError, setHasError] = useState(false);
    const isDark = theme === 'dark';

    useEffect(() => {
        let cancelled = false;
        setResultState(null);
        setIsLoading(true);
        setHasError(false);

        const loadTicket = async () => {
            try {
                if (!currentUser) throw new Error('User is not authenticated');
                const authToken = await currentUser.getIdToken();
                const result = await getTicketById(publicId, authToken);
                if (!result) throw new Error('Ticket was not found');
                if (!cancelled) setResultState({ value: result, scope: authScope, reference: publicId });
            } catch {
                if (!cancelled) setHasError(true);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        };

        void loadTicket();
        return () => { cancelled = true; };
    }, [currentUser, authScope, publicId]);

    const primary = isDark ? 'text-slate-100' : 'text-slate-800';
    const secondary = isDark ? 'text-slate-400' : 'text-slate-500';
    const divider = isDark ? 'border-white/[0.07]' : 'border-slate-100';

    return (
        <div className="w-72 p-1 font-poppins">
            <div className={`border-b pb-3 ${divider}`}>
                <div className={`font-mono text-[11px] font-bold uppercase tracking-wide ${isDark ? 'text-violet-300' : 'text-[#7E57C2]'}`}>#{publicId}</div>
                <div className={`mt-1 line-clamp-2 text-sm font-semibold leading-5 ${primary}`}>
                    {ticket?.title ?? (isLoading ? 'Loading ticket…' : 'Ticket unavailable')}
                </div>
            </div>

            <div className="relative flex min-h-[72px] flex-col justify-center gap-2 pt-3 text-xs">
                {isLoading ? (
                    <div className={`flex items-center justify-center gap-2 ${secondary}`} role="status">
                        <Loader2 size={16} className="animate-spin" /> Loading details…
                    </div>
                ) : hasError || !ticket ? (
                    <div className="flex items-center justify-center gap-2 text-amber-600 dark:text-amber-300">
                        <AlertCircle size={16} /> Ticket details could not be loaded.
                    </div>
                ) : (
                    <>
                        <div className="flex items-center justify-between gap-3">
                            <span className={`flex items-center gap-1.5 ${secondary}`}><CircleDot size={14} /> Status</span>
                            <span className={`truncate font-semibold ${primary}`}>{ticket.status || 'Open'}</span>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                            <span className={`flex items-center gap-1.5 ${secondary}`}><Gauge size={14} /> Priority</span>
                            <span className={`truncate ${primary}`}>{ticket.priority || 'Not set'}</span>
                        </div>
                        <div className="flex items-center justify-between gap-3">
                            <span className={`flex items-center gap-1.5 ${secondary}`}><UserRound size={14} /> Assignee</span>
                            <span className={`max-w-[150px] truncate ${primary}`}>
                                {ticket.assignee?.displayName || 'Unassigned'}
                            </span>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default TicketProfileTooltip;
